import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import NavBar from "../../../Common/NavBar";
import { CinturonModal } from "./CinturonModal";
import { requestDataCinturon } from "./requestDataCinturon";
import { CinturonesSeguridadCatalogoURL, EmpresasURL, VehiculosURL } from "../../../API/apiurls";

export function CinturonesAdmin() {
  const navigation = useNavigate();
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [vehiculoId, setVehiculoId] = useState("");
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadEmpresas = async () => {
    const res = await axios.get(EmpresasURL);
    const list = Array.isArray(res.data) ? res.data : [];
    setEmpresas(list);
    setEmpresaId((prev) => prev || String(list[0]?.id ?? ""));
  };

  const loadVehiculos = async (empresa) => {
    if (!empresa) {
      setVehiculos([]);
      return;
    }
    const res = await axios.get(`${VehiculosURL}/xempresa/${empresa}`);
    setVehiculos(Array.isArray(res.data) ? res.data : []);
  };

  const loadCatalogo = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (empresaId) params.empresaId = Number(empresaId);
      if (vehiculoId) params.vehiculoId = Number(vehiculoId);
      const res = await axios.get(CinturonesSeguridadCatalogoURL, { params });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error cargando catálogo de cinturones:", error);
    } finally {
      setLoading(false);
    }
  }, [empresaId, vehiculoId]);

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    loadVehiculos(empresaId);
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId) return;
    loadCatalogo();
  }, [empresaId, vehiculoId, loadCatalogo]);

  const empresaNombre = useMemo(() => empresas.find((empresa) => String(empresa.id) === String(empresaId))?.nombre || "-", [empresas, empresaId]);

  const guardar = async (data) => {
    try {
      const payload = requestDataCinturon(data);
      await axios.post(CinturonesSeguridadCatalogoURL, payload);
      setShow(false);
      setEditItem(null);
      loadCatalogo();
      Swal.fire("Listo", "Cinturón creado", "success");
    } catch (error) {
      console.error("Error creando cinturón:", error);
      Swal.fire("Error", "No se pudo crear el cinturón", "error");
    }
  };

  const editar = async (data) => {
    try {
      const payload = requestDataCinturon(data);
      await axios.put(`${CinturonesSeguridadCatalogoURL}/${data.id}`, payload);
      setShow(false);
      setEditItem(null);
      loadCatalogo();
      Swal.fire("Listo", "Cinturón actualizado", "success");
    } catch (error) {
      console.error("Error editando cinturón:", error);
      Swal.fire("Error", "No se pudo editar el cinturón", "error");
    }
  };

  const eliminar = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar cinturón?",
      text: "Se dará de baja en el catálogo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${CinturonesSeguridadCatalogoURL}/${id}`);
      loadCatalogo();
      Swal.fire("Listo", "Cinturón eliminado", "success");
    } catch (error) {
      console.error("Error eliminando cinturón:", error);
      Swal.fire("Error", "No se pudo eliminar el cinturón", "error");
    }
  };

  return (
    <div>
      <NavBar />
      <div className="camionesMenu-contenedor admin-crud-shell">
        <Button style={{ width: "100%", marginBottom: 12 }} onClick={() => navigation("/panel-administrador")}>Atrás</Button>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, marginBottom: 16 }}>
          <select value={empresaId} onChange={(e) => { setEmpresaId(e.target.value); setVehiculoId(""); }} className="form-select bg-dark text-light border-secondary">
            {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>)}
          </select>
          <select value={vehiculoId} onChange={(e) => setVehiculoId(e.target.value)} className="form-select bg-dark text-light border-secondary">
            <option value="">Todos los vehículos</option>
            {vehiculos.map((vehiculo) => <option key={vehiculo.id} value={vehiculo.id}>{vehiculo.nombre}</option>)}
          </select>
          <Button onClick={() => { setEditItem(null); setShow(true); }}>Agregar cinturón</Button>
        </div>

        <div style={{ color: "white", marginBottom: 12 }}>Empresa: <strong>{empresaNombre}</strong></div>

        <div className="table-responsive">
          <Table striped bordered hover variant="dark">
            <thead>
              <tr>
                <th>ID</th>
                <th>Serial</th>
                <th>Nombre</th>
                <th>Vehículo</th>
                <th>Posición</th>
                <th>Asiento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!items.length && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>{loading ? "Cargando..." : "Sin registros"}</td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.serialNumero}</td>
                  <td>{item.nombre}</td>
                  <td>{item.vehiculosModel?.nombre ?? "-"}</td>
                  <td>{item.posicionModel?.nombre ?? "-"}</td>
                  <td>{item.asiento ?? "-"}</td>
                  <td>{item.activo ? "Activo" : "Inactivo"}</td>
                  <td>
                    <Button variant="warning" style={{ marginRight: 8 }} onClick={() => { setEditItem(item); setShow(true); }}>Editar</Button>
                    <Button variant="danger" onClick={() => eliminar(item.id)}>Eliminar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <CinturonModal
        show={show}
        onHide={() => { setShow(false); setEditItem(null); }}
        initialData={editItem || { empresaId }}
        onSave={editItem ? editar : guardar}
        title={editItem ? "Editar cinturón" : "Nuevo cinturón"}
      />
    </div>
  );
}
